import {
    LEGAL_CONTACT_EMAIL,
    LEGAL_DEMO_EMAIL,
    LEGAL_EFFECTIVE_DATE,
    LEGAL_GRIEVANCE_EMAIL,
    LEGAL_GRIEVANCE_NAME,
    LEGAL_POLICY_VERSION,
    LEGAL_PRIVACY_EMAIL,
    LEGAL_REGISTERED_OFFICE,
} from './policyVersion';
import type { LegalDocumentBody, LegalSection } from './legalDocuments';

const PLACEHOLDERS: Record<string, string> = {
    '{{LEGAL_POLICY_VERSION}}': LEGAL_POLICY_VERSION,
    '{{LEGAL_EFFECTIVE_DATE}}': LEGAL_EFFECTIVE_DATE,
    '{{LEGAL_CONTACT_EMAIL}}': LEGAL_CONTACT_EMAIL,
    '{{LEGAL_DEMO_EMAIL}}': LEGAL_DEMO_EMAIL,
    '{{LEGAL_PRIVACY_EMAIL}}': LEGAL_PRIVACY_EMAIL,
    '{{LEGAL_GRIEVANCE_NAME}}': LEGAL_GRIEVANCE_NAME,
    '{{LEGAL_GRIEVANCE_EMAIL}}': LEGAL_GRIEVANCE_EMAIL,
    '{{LEGAL_REGISTERED_OFFICE}}': LEGAL_REGISTERED_OFFICE,
};

function applyPlaceholders(text: string): string {
    let out = text;
    for (const [token, value] of Object.entries(PLACEHOLDERS)) {
        out = out.split(token).join(value);
    }
    return out;
}

function parseFrontmatter(raw: string): { meta: Record<string, string>; body: string } {
    const trimmed = raw.replace(/^\uFEFF/, '');
    if (!trimmed.startsWith('---')) {
        return { meta: {}, body: trimmed };
    }
    const end = trimmed.indexOf('\n---', 3);
    if (end < 0) {
        return { meta: {}, body: trimmed };
    }
    const fm = trimmed.slice(4, end).trim();
    const body = trimmed.slice(end + 4).replace(/^\s*\n/, '');
    const meta: Record<string, string> = {};
    for (const line of fm.split('\n')) {
        const idx = line.indexOf(':');
        if (idx < 0) continue;
        meta[line.slice(0, idx).trim()] = line.slice(idx + 1).trim().replace(/^["']|["']$/g, '');
    }
    return { meta, body };
}

export function parseLegalMarkdown(raw: string): LegalDocumentBody {
    const { meta, body } = parseFrontmatter(applyPlaceholders(raw));
    const counselSigned = applyPlaceholders(meta.counsel_signed || 'false').toLowerCase() === 'true';
    const lines = body.replace(/\r\n/g, '\n').split('\n');
    let title = applyPlaceholders(meta.title || '');
    let intro = '';
    const sections: LegalSection[] = [];
    let current: LegalSection | null = null;
    let paraBuf: string[] = [];

    const flushPara = () => {
        const text = paraBuf.join(' ').trim();
        paraBuf = [];
        if (!text) return;
        if (current) current.paragraphs.push(text);
        else intro = intro ? `${intro} ${text}` : text;
    };

    for (const line of lines) {
        if (line.startsWith('# ') && !title) {
            title = line.slice(2).trim();
            continue;
        }
        if (line.startsWith('## ')) {
            flushPara();
            current = { heading: line.slice(3).trim(), paragraphs: [] };
            sections.push(current);
            continue;
        }
        if (line.startsWith('- ') && current) {
            flushPara();
            current.bullets = current.bullets || [];
            current.bullets.push(line.slice(2).trim());
            continue;
        }
        if (!line.trim()) {
            flushPara();
            continue;
        }
        paraBuf.push(line.trim());
    }
    flushPara();

    return {
        title: title || 'Legal',
        version: applyPlaceholders(meta.version || LEGAL_POLICY_VERSION),
        effectiveDate: applyPlaceholders(meta.effective || LEGAL_EFFECTIVE_DATE),
        intro,
        counselSigned,
        sections,
    };
}
